import React, { useState, useCallback, useEffect } from 'react';
import { Tabs, Spin, message } from 'antd';
import './PWP252Form.css';

import { usePWP252Mode } from './hooks/usePWP252Mode';
import { usePWP252Record } from './hooks/usePWP252Record';
import { usePWP252Navigation } from './hooks/usePWP252Navigation';
import ActionBar from './components/ActionBar';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Details from './components/Tab2Details';
import type { FP620, FP621 } from '../../services/pwp252Api';

const { TabPane } = Tabs;

const EMPTY_MASTER: Partial<FP620> = {
  fp620_01: '',
  fp620_10: '',
  fp620_11: new Date().toISOString(),
  fp620_12: '',
  fp620_13: '',
  fp620_14: '',
  fp620_141: '',
  fp620_15: '',
  fp620_16: 'CM*CM*CM(YDS)',
  fp620_17: '',
  fp620_18: 0,
  fp620_22: 0.9144,
  fp620_23: 0,
  fp620_24: 0,
  fp620_25: 1,
  fp620_28: 1,
  fp620_29: '',
  fp620_91: '',
  fp620_93: '',
};

const PWP252Form: React.FC = () => {
  const { mode, toView, toAdd, toEdit, isEditing } = usePWP252Mode();
  const { record, loading, load, save, remove, setRecord } = usePWP252Record();
  const { list, hasPrev, hasNext, fetchList, goFirst, goLast, goPrev, goNext, setCurrentById } =
    usePWP252Navigation();

  const [formData, setFormData] = useState<Partial<FP620>>(EMPTY_MASTER);
  const [details, setDetails] = useState<Partial<FP621>[]>([]);
  const [activeTab, setActiveTab] = useState('1');
  const [gwVariation, setGwVariation] = useState(0);

  // Sync record to form
  useEffect(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    }
  }, [record]);

  const loadRecord = useCallback(async (id: string) => {
    const result = await load(id);
    if (result) {
      setCurrentById(id);
    }
    return result;
  }, [load, setCurrentById]);

  // Navigation
  const handleFirst = useCallback(async () => {
    const id = goFirst();
    if (id) await loadRecord(id);
  }, [goFirst, loadRecord]);

  const handlePrev = useCallback(async () => {
    const id = goPrev();
    if (id) await loadRecord(id);
  }, [goPrev, loadRecord]);

  const handleNext = useCallback(async () => {
    const id = goNext();
    if (id) await loadRecord(id);
  }, [goNext, loadRecord]);

  const handleLast = useCallback(async () => {
    const id = goLast();
    if (id) await loadRecord(id);
  }, [goLast, loadRecord]);

  // Mode handlers
  const handleAdd = useCallback(() => {
    setFormData({ ...EMPTY_MASTER, fp620_11: new Date().toISOString() });
    setDetails([]);
    setRecord(null);
    toAdd();
    setActiveTab('1');
  }, [toAdd, setRecord]);

  const handleEdit = useCallback(() => {
    if (!record) return;
    setFormData({ ...record });
    setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    toEdit();
  }, [record, toEdit]);

  const handleCancel = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    } else {
      setFormData({ ...EMPTY_MASTER });
      setDetails([]);
    }
    toView();
  }, [record, toView]);

  const handleSave = useCallback(async () => {
    const isNew = mode === 'add';
    const result = await save(formData, details, isNew);
    if (result) {
      await fetchList(1);
      toView();
    }
  }, [formData, details, mode, save, fetchList, toView]);

  const handleDelete = useCallback(async () => {
    if (!record) return;
    const ok = await remove(record.fp620_01);
    if (ok) {
      setFormData({ ...EMPTY_MASTER });
      setDetails([]);
      await fetchList(1);
      toView();
    }
  }, [record, remove, fetchList, toView]);

  const handleFieldChange = useCallback((field: keyof FP620, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleDetailsChange = useCallback((next: Partial<FP621>[]) => {
    setDetails(next);
    setFormData(prev => ({ ...prev, fp620_18: next.length }));
  }, []);

  return (
    <div className="pwp252-form">
      <h2 style={{ margin: '0 0 8px 0', fontSize: 16 }}>PACKING-貨櫃</h2>

      <ActionBar
        mode={mode}
        hasRecord={!!record}
        hasPrev={hasPrev}
        hasNext={hasNext}
        loading={loading}
        onFirst={handleFirst}
        onPrev={handlePrev}
        onNext={handleNext}
        onLast={handleLast}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
        onDelete={handleDelete}
      />

      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本資料" key="1">
            <Tab1BasicInfo
              formData={formData}
              isEditing={isEditing}
              onChange={handleFieldChange}
            />
          </TabPane>
          <TabPane tab="明細" key="2">
            <Tab2Details
              formData={formData}
              details={details}
              shippingUnit={formData.fp620_25 || 1}
              isEditing={isEditing}
              onChange={handleDetailsChange}
              onFieldChange={handleFieldChange}
              gwVariation={gwVariation}
              onGwVariationChange={setGwVariation}
            />
          </TabPane>
        </Tabs>
      </Spin>
    </div>
  );
};

export default PWP252Form;
